import React, { useEffect, useRef, useState } from 'react';
import { api, API, formatApiErrorDetail } from '../lib/api';
import { useRegisterStatus, humanFreshness } from '../context/RegisterStatus';
import { useProperty, resolveAsset } from '../context/PropertyContext';
import { CheckCircle2, XCircle, RefreshCw, Loader2, Upload, FileDown, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

function StatusPill({ ok, label }) {
  const cfg = ok
    ? { bg: '#E8F5E9', fg: '#1F5A24', Icon: CheckCircle2, txt: label || 'Healthy' }
    : { bg: '#FDECEA', fg: '#8B1A1A', Icon: XCircle, txt: label || 'Unavailable' };
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium" style={{ background: cfg.bg, color: cfg.fg }}>
      <cfg.Icon size={16} aria-hidden /> {cfg.txt}
    </span>
  );
}

function PropertyCard() {
  const { property, refresh, setProperty } = useProperty() || {};
  const [form, setForm] = useState({ name: '', address: '', reception_phone: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (property) setForm({
      name: property.name || '',
      address: property.address || '',
      reception_phone: property.reception_phone || '',
    });
  }, [property]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch('/property', form);
      setProperty(data);
      toast.success('Property details saved');
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const { data } = await api.post('/property/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refresh();
      toast.success('Logo updated');
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const logoUrl = property?.logo_url ? resolveAsset(property.logo_url) : '';

  return (
    <section className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl p-5 mb-5">
      <h2 className="font-heading text-xl text-[#1F5A24]">Property details</h2>
      <p className="text-sm text-[#333]/70 mt-1">These appear on the sign-in screen, the header, and printed arrivals sheets.</p>

      <div className="mt-5 grid gap-5 sm:grid-cols-[auto,1fr] items-start">
        <div className="flex flex-col items-center gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt="Property logo" className="w-24 h-24 object-contain rounded-xl bg-white border border-[#CFD8CF] p-2" />
          ) : (
            <div className="w-24 h-24 rounded-xl bg-[#E8F5E9] text-[#1F5A24] grid place-items-center font-heading text-3xl border border-[#CFD8CF]" aria-hidden>
              {(form.name || 'B').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              data-testid="property-logo-upload"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#CFD8CF] hover:bg-[#F5F5F5] text-sm disabled:opacity-60"
            >
              <Upload size={14}/> {uploading ? 'Uploading…' : 'Upload logo'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </div>
        </div>

        <form onSubmit={save} className="grid gap-3">
          <div>
            <label htmlFor="prop-name" className="block text-xs uppercase tracking-widest text-[#333]/60 mb-1">Property name</label>
            <input
              id="prop-name"
              data-testid="property-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Casa Curaçao Guesthouse"
              className="w-full bg-white border border-[#CFD8CF] rounded-lg px-4 py-3 min-h-[3rem]"
              maxLength={120}
            />
          </div>
          <div>
            <label htmlFor="prop-addr" className="block text-xs uppercase tracking-widest text-[#333]/60 mb-1">Address</label>
            <textarea
              id="prop-addr"
              data-testid="property-address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              rows={2}
              className="w-full bg-white border border-[#CFD8CF] rounded-lg px-4 py-3"
              maxLength={500}
            />
          </div>
          <div>
            <label htmlFor="prop-phone" className="block text-xs uppercase tracking-widest text-[#333]/60 mb-1">Reception phone</label>
            <input
              id="prop-phone"
              data-testid="property-phone"
              value={form.reception_phone}
              onChange={(e) => setForm({ ...form, reception_phone: e.target.value })}
              placeholder="+5999 …"
              className="w-full bg-white border border-[#CFD8CF] rounded-lg px-4 py-3 min-h-[3rem]"
              maxLength={60}
            />
          </div>
          <div>
            <button
              data-testid="property-save"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-3 min-h-[3rem] rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium disabled:opacity-60"
            >
              {saving ? <Loader2 className="animate-spin" size={16}/> : null}
              Save
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function RoomsCard() {
  const { property, setProperty } = useProperty() || {};
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const keyCounter = useRef(0);
  const nextKey = () => { keyCounter.current += 1; return `row-${keyCounter.current}`; };

  useEffect(() => {
    if (property) {
      const r = (property.rooms || []).map((x) => ({
        key: nextKey(),
        room_type: x.room_type || '',
        capacity: x.capacity ?? 1,
      }));
      setRows(r);
    }
  }, [property]);

  const add = () => setRows((rs) => [...rs, { key: nextKey(), room_type: '', capacity: 1 }]);
  const remove = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const change = (i, key, value) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [key]: value } : r));

  const save = async () => {
    const clean = rows
      .map((r) => ({ room_type: (r.room_type || '').trim(), capacity: Math.max(0, parseInt(r.capacity || 0, 10) || 0) }))
      .filter((r) => r.room_type);
    setSaving(true);
    try {
      const { data } = await api.patch('/property', { rooms: clean });
      setProperty(data);
      toast.success('Rooms list saved');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <section className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl p-5 mb-5" data-testid="rooms-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-xl text-[#1F5A24]">Rooms</h2>
          <p className="text-sm text-[#333]/70 mt-1">
            Name each room type and how many are bookable. These drive the occupancy calendar.
          </p>
        </div>
        <button
          type="button"
          data-testid="rooms-add"
          onClick={add}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#CFD8CF] hover:bg-[#E8F5E9] text-sm text-[#1F5A24]"
        >
          <Plus size={14}/> Add room type
        </button>
      </div>

      <div className="mt-4 space-y-2">
        {rows.length === 0 && (
          <div className="text-sm text-[#333]/60 py-6 text-center border border-dashed border-[#CFD8CF] rounded-lg">
            No rooms yet. Add your first one to see it in the calendar.
          </div>
        )}
        {rows.map((r, i) => (
          <div key={r.key} className="grid grid-cols-[1fr,7rem,auto] gap-2 items-center">
            <input
              data-testid={`rooms-name-${i}`}
              value={r.room_type}
              onChange={(e) => change(i, 'room_type', e.target.value)}
              placeholder="e.g. Ocean View Double"
              className="bg-white border border-[#CFD8CF] rounded-lg px-3 py-2.5 min-h-[3rem]"
              maxLength={120}
            />
            <input
              data-testid={`rooms-capacity-${i}`}
              type="number"
              min={0}
              value={r.capacity}
              onChange={(e) => change(i, 'capacity', e.target.value)}
              placeholder="Capacity"
              className="bg-white border border-[#CFD8CF] rounded-lg px-3 py-2.5 min-h-[3rem]"
            />
            <button
              type="button"
              data-testid={`rooms-remove-${i}`}
              onClick={() => remove(i)}
              className="p-2.5 rounded-lg border border-[#CFD8CF] hover:bg-[#FDECEA] hover:text-[#8B1A1A]"
              aria-label="Remove room"
            >
              <Trash2 size={16}/>
            </button>
          </div>
        ))}
      </div>

      <div className="mt-4">
        <button
          data-testid="rooms-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-3 min-h-[3rem] rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium disabled:opacity-60"
        >
          {saving ? <Loader2 className="animate-spin" size={16}/> : null}
          Save rooms
        </button>
      </div>
    </section>
  );
}

export default function Settings() {
  const s = useRegisterStatus();

  return (
    <div className="max-w-3xl">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#1F5A24]">Settings</h1>
          <p className="text-[#333]/70 mt-1">Property details and the health of every connection this console depends on.</p>
        </div>
        <button
          data-testid="download-recommendations"
          onClick={async () => {
            try {
              const res = await fetch(`${API}/reports/recommendations`, {
                credentials: 'include',
                headers: { Authorization: `Bearer ${localStorage.getItem('bb_token') || ''}` },
              });
              if (!res.ok) throw new Error('failed');
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `bon-bini-recommendations-${new Date().toISOString().slice(0, 10)}.md`;
              a.click();
              URL.revokeObjectURL(url);
            } catch { toast.error('Could not download the report'); }
          }}
          className="inline-flex items-center gap-2 px-4 py-3 min-h-[3rem] rounded-lg border border-[#CFD8CF] hover:bg-[#E8F5E9] text-[#1F5A24] font-medium"
        >
          <FileDown size={16} aria-hidden/> Recommendations report
        </button>
      </div>

      <PropertyCard />
      <RoomsCard />

      <section className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl p-5 mb-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl text-[#1F5A24]">Booking register</h2>
            <p className="text-sm text-[#333]/70 mt-1">The shared list of bookings this console reads from.</p>
          </div>
          <StatusPill ok={!!s?.status?.healthy && !s?.isStale} label={s?.isStale ? 'Stale' : 'Healthy'} />
        </div>
        <dl className="grid sm:grid-cols-2 gap-4 mt-5 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-widest text-[#333]/60">Last read</dt>
            <dd data-testid="settings-last-read" className="mt-0.5">
              {s?.status?.last_read_at ? `${humanFreshness(s.freshness)} (${new Date(s.status.last_read_at).toLocaleString('en-GB')})` : 'never'}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-widest text-[#333]/60">Refresh cadence</dt>
            <dd className="mt-0.5">every {s?.status?.cache_ttl_seconds ?? '—'} seconds, or on demand</dd>
          </div>
          {s?.status?.last_error && (
            <div className="sm:col-span-2">
              <dt className="text-xs uppercase tracking-widest text-[#B26A00]">Most recent problem</dt>
              <dd className="mt-0.5 text-[#8f5500]">{s.status.last_error}</dd>
            </div>
          )}
        </dl>
        <div className="mt-5">
          <button
            data-testid="settings-refresh-register"
            onClick={s?.refresh}
            disabled={s?.refreshing}
            className="inline-flex items-center gap-2 px-4 py-3 min-h-[3rem] rounded-lg bg-[#2E7D32] hover:bg-[#1F5A24] text-white font-medium disabled:opacity-60"
          >
            <RefreshCw size={16} className={s?.refreshing ? 'animate-spin' : ''} aria-hidden /> Refresh now
          </button>
        </div>
      </section>

      <section className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl text-[#1F5A24]">Guest notifications</h2>
            <p className="text-sm text-[#333]/70 mt-1">
              After a payment is confirmed here, a background process reads the register and sends
              the confirmation message to the guest, usually within two minutes. The console itself
              does not call any external service.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
